// Moving, resizing and rotating the selection (shared by the select and lasso tools).
import type { LineElement, SepiaElement } from "../../../shared/model";
import type { Editor, PointerInfo } from "../editor";
import { bounds, boxOf, routeConnector, transformElement, type Xform } from "../elements";
import { type Box, type Vec, center, rotate, dist } from "../geometry";

export type Handle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "rot" | "p0" | "p1" | "pm";

const CORNERS: Record<string, [number, number]> = {
  nw: [0, 0], n: [0.5, 0], ne: [1, 0], e: [1, 0.5], se: [1, 1], s: [0.5, 1], sw: [0, 1], w: [0, 0.5]
};

/** Screen positions of the selection handles. */
export function handlePositions(editor: Editor): { h: Handle; p: Vec }[] {
  const sel = editor.selected();
  if (!sel.length || editor.ui.editingId) return [];
  const cam = editor.camera;
  if (sel.length === 1 && sel[0].type === "line") {
    const line = sel[0] as LineElement;
    const pts = line.pts.map(([x, y]) => cam.toScreen({ x: line.x + x, y: line.y + y }));
    const out: { h: Handle; p: Vec }[] = [{ h: "p0", p: pts[0] }, { h: "p1", p: pts[pts.length - 1] }];
    const mid = pts.length > 2 ? pts[1] : { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
    if (line.route !== "elbow") out.push({ h: "pm", p: mid });
    return out;
  }
  const frame = editor.selectionFrame();
  if (!frame || sel.every((e) => e.locked)) return [];
  const { box, rot } = frame;
  const c = center(box);
  const out: { h: Handle; p: Vec }[] = [];
  const small = box.w * cam.z < 44 || box.h * cam.z < 44;
  for (const [h, [fx, fy]] of Object.entries(CORNERS)) {
    if (small && (h === "n" || h === "s" || h === "e" || h === "w")) continue;
    out.push({ h: h as Handle, p: cam.toScreen(rotate({ x: box.x + box.w * fx, y: box.y + box.h * fy }, rot, c)) });
  }
  const top = rotate({ x: box.x + box.w / 2, y: box.y }, rot, c);
  const up = rotate({ x: 0, y: -1 }, rot);
  const ts = cam.toScreen(top);
  out.push({ h: "rot", p: { x: ts.x + up.x * 30, y: ts.y + up.y * 30 } });
  return out;
}

export function handleAt(editor: Editor, screen: Vec, kind: PointerInfo["kind"]): Handle | null {
  const r = kind === "mouse" ? 9 : 18;
  let best: Handle | null = null;
  let bestD = Infinity;
  for (const { h, p } of handlePositions(editor)) {
    const d = dist(p, screen);
    if (d <= r && d < bestD) {
      best = h;
      bestD = d;
    }
  }
  return best;
}

export function cursorForHandle(h: Handle, rot: number): string {
  if (h === "rot") return "grab";
  if (h === "p0" || h === "p1" || h === "pm") return "move";
  const angles: Record<string, number> = { e: 0, se: 45, s: 90, sw: 135, w: 180, nw: 225, n: 270, ne: 315 };
  const a = (angles[h] + (rot * 180) / Math.PI + 360) % 180;
  const names = ["ew-resize", "nwse-resize", "ns-resize", "nesw-resize"];
  return names[Math.round(a / 45) % 4];
}

interface SnapCandidates {
  xs: number[];
  ys: number[];
}

export class TransformSession {
  private originals: SepiaElement[];
  private frame: { box: Box; rot: number };
  private startWorld: Vec;
  private snaps: SnapCandidates | null = null;
  private moved = false;
  private duplicated = false;
  private lastPreview: SepiaElement[] = [];
  private connectors: SepiaElement[];

  constructor(private editor: Editor, private mode: "move" | "resize" | "rotate" | "point", start: PointerInfo, private handle: Handle | null = null) {
    this.startWorld = start.world;
    const sel = editor.selected().filter((e) => !e.locked);
    // frames carry the elements inside them
    const extra: SepiaElement[] = [];
    if (mode === "move") {
      const ids = new Set(sel.map((e) => e.id));
      for (const f of sel) if (f.type === "frame") for (const child of editor.frameChildren(f.id)) if (!ids.has(child.id) && !child.locked) {
        ids.add(child.id);
        extra.push(child);
      }
    }
    this.originals = [...sel, ...extra];
    this.frame = editor.selectionFrame() ?? { box: { x: 0, y: 0, w: 1, h: 1 }, rot: 0 };
    const ids = new Set(this.originals.map((e) => e.id));
    this.connectors = editor.doc.all().filter(
      (e) => e.type === "line" && !ids.has(e.id) && ((e.start.bind && ids.has(e.start.bind)) || (e.end.bind && ids.has(e.end.bind)))
    );
    if (mode === "move" && this.originals.some((e) => e.type !== "ink")) this.snaps = this.collectSnaps(ids);
  }

  get isEmpty(): boolean {
    return this.originals.length === 0;
  }

  private collectSnaps(exclude: Set<string>): SnapCandidates {
    const view = this.editor.camera.view(200);
    const xs: number[] = [];
    const ys: number[] = [];
    for (const el of this.editor.doc.all()) {
      if (exclude.has(el.id) || el.type === "ink" || el.type === "line" || !this.editor.isShown(el)) continue;
      const b = bounds(el);
      if (b.x > view.x + view.w || b.x + b.w < view.x || b.y > view.y + view.h || b.y + b.h < view.y) continue;
      xs.push(b.x, b.x + b.w / 2, b.x + b.w);
      ys.push(b.y, b.y + b.h / 2, b.y + b.h);
    }
    return { xs, ys };
  }

  update(p: PointerInfo): void {
    const ed = this.editor;
    const z = ed.camera.z;
    let dx = p.world.x - this.startWorld.x;
    let dy = p.world.y - this.startWorld.y;
    if (!this.moved && Math.hypot(dx, dy) * z < (p.kind === "mouse" ? 2 : 4)) return;
    if (!this.moved) {
      this.moved = true;
      ed.setUi({ busy: true });
      if (this.mode === "move" && p.alt && !this.duplicated) this.duplicateInPlace();
    }
    let xf: Xform;
    const guides: { x?: number; y?: number }[] = [];

    if (this.mode === "move") {
      if (p.shift) {
        if (Math.abs(dx) > Math.abs(dy)) dy = 0;
        else dx = 0;
      }
      if (this.snaps) {
        const b = this.selectionBox();
        const tol = 6 / z;
        const bestX = bestSnap([b.x + dx, b.x + b.w / 2 + dx, b.x + b.w + dx], this.snaps.xs, tol);
        const bestY = bestSnap([b.y + dy, b.y + b.h / 2 + dy, b.y + b.h + dy], this.snaps.ys, tol);
        if (bestX) {
          dx += bestX.delta;
          guides.push({ x: bestX.at });
        }
        if (bestY) {
          dy += bestY.delta;
          guides.push({ y: bestY.at });
        }
      } else if (ed.settings.snapToGrid) {
        const g = ed.bg.size;
        const b = this.selectionBox();
        dx = Math.round((b.x + dx) / g) * g - b.x;
        dy = Math.round((b.y + dy) / g) * g - b.y;
      }
      xf = { kind: "move", dx, dy };
    } else if (this.mode === "rotate") {
      const c = center(this.frame.box);
      let a = Math.atan2(p.world.y - c.y, p.world.x - c.x) - Math.atan2(this.startWorld.y - c.y, this.startWorld.x - c.x);
      const total = this.frame.rot + a;
      const step = Math.PI / 12;
      const snapped = Math.round(total / step) * step;
      if (p.shift || Math.abs(snapped - total) < 0.035) a = snapped - this.frame.rot;
      xf = { kind: "rotate", angle: a, origin: c };
    } else if (this.mode === "resize") {
      xf = this.resizeXform(p);
    } else {
      this.updatePoint(p);
      return;
    }

    const moved = this.originals.map((e) => transformElement(e, xf));
    const lookup = new Map(moved.map((e) => [e.id, e]));
    const get = (id: string) => lookup.get(id) ?? ed.doc.get(id);
    const routed = this.connectors.map((c) => routeConnector(c as LineElement, get));
    this.lastPreview = [...moved, ...routed];
    ed.setTransient(this.lastPreview);
    ed.setOverlay({ guides });
  }

  private selectionBox(): Box {
    const bs = this.originals.map(bounds);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const b of bs) {
      x0 = Math.min(x0, b.x);
      y0 = Math.min(y0, b.y);
      x1 = Math.max(x1, b.x + b.w);
      y1 = Math.max(y1, b.y + b.h);
    }
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  private resizeXform(p: PointerInfo): Xform {
    const { box, rot } = this.frame;
    const c = center(box);
    const h = this.handle!;
    const [fx, fy] = CORNERS[h];
    const anchorLocal = { x: box.x + box.w * (1 - fx), y: box.y + box.h * (1 - fy) };
    const q = rotate(p.world, -rot, c);
    const minW = 6 / this.editor.camera.z;
    let sx = 1;
    let sy = 1;
    if (fx !== 0.5) sx = (q.x - anchorLocal.x) / ((fx === 1 ? 1 : -1) * Math.max(box.w, 1e-6));
    if (fy !== 0.5) sy = (q.y - anchorLocal.y) / ((fy === 1 ? 1 : -1) * Math.max(box.h, 1e-6));
    const corner = fx !== 0.5 && fy !== 0.5;
    const keepAspect =
      p.shift !== (corner && this.originals.every((e) => e.type === "image" || e.type === "sticker" || e.type === "text"));
    if (keepAspect && corner) {
      const s = Math.max(Math.abs(sx), Math.abs(sy));
      sx = Math.sign(sx || 1) * s;
      sy = Math.sign(sy || 1) * s;
    } else if (keepAspect && !corner) {
      if (fx === 0.5) sx = Math.abs(sy);
      else sy = Math.abs(sx);
    }
    const allowFlip = this.originals.every((e) => e.type === "ink" || e.type === "line");
    if (!allowFlip) {
      sx = Math.max(sx, minW / Math.max(box.w, 1e-6));
      sy = Math.max(sy, minW / Math.max(box.h, 1e-6));
    }
    if (Math.abs(sx * box.w) < minW) sx = Math.sign(sx || 1) * (minW / Math.max(box.w, 1e-6));
    if (Math.abs(sy * box.h) < minW) sy = Math.sign(sy || 1) * (minW / Math.max(box.h, 1e-6));
    return { kind: "scale", sx, sy, origin: rotate(anchorLocal, rot, c), frameRot: rot };
  }

  /** Dragging a connector end (and re-attaching it) or bending its middle. */
  private updatePoint(p: PointerInfo): void {
    const ed = this.editor;
    const line = this.originals[0] as LineElement;
    const world = line.pts.map(([x, y]) => ({ x: line.x + x, y: line.y + y }));
    let next: LineElement;
    if (this.handle === "pm") {
      const mid = p.world;
      const pts = world.length > 2 ? [world[0], mid, world[world.length - 1]] : [world[0], mid, world[1]];
      next = rebuild(line, pts, { route: "curve" });
      ed.setOverlay({ anchorHint: null });
    } else {
      const which = this.handle === "p0" ? "start" : "end";
      const target = ed.hitAt(p.world, 6, { filter: (e) => e.id !== line.id && e.type !== "line" && e.type !== "ink" });
      const pts = [...world];
      pts[which === "start" ? 0 : pts.length - 1] = p.world;
      next = rebuild(line, pts, {});
      if (target) {
        const tb = boxOf(target);
        const local = rotate(p.world, -target.rot, center(tb));
        const ax = Math.min(1, Math.max(0, (local.x - tb.x) / tb.w));
        const ay = Math.min(1, Math.max(0, (local.y - tb.y) / tb.h));
        const inner = ax > 0.2 && ax < 0.8 && ay > 0.2 && ay < 0.8;
        next = { ...next, [which]: { ...next[which], bind: target.id, ax: inner ? 0.5 : ax, ay: inner ? 0.5 : ay } };
        next = routeConnector(next, (id) => ed.doc.get(id));
        ed.setOverlay({ anchorHint: { id: target.id, p: p.world } });
      } else {
        next = { ...next, [which]: { ...next[which], bind: null } };
        ed.setOverlay({ anchorHint: null });
      }
    }
    this.lastPreview = [next];
    ed.setTransient(this.lastPreview);
  }

  private duplicateInPlace(): void {
    // Alt-drag: leave copies behind and move the originals
    const copies = this.editor.cloneElements(this.originals, 0, 0);
    this.editor.commit({ puts: copies }, { key: "dup-drag" });
    this.duplicated = true;
  }

  /** Commit the gesture. Returns false if nothing moved (a plain click). */
  finish(): boolean {
    const ed = this.editor;
    ed.setOverlay({ guides: [], anchorHint: null });
    ed.setUi({ busy: false });
    if (!this.moved || !this.lastPreview.length) {
      ed.setTransient(null);
      return false;
    }
    const now = Date.now();
    ed.commit({ puts: this.lastPreview.map((e) => ({ ...e, t: now })) });
    ed.setTransient(null);
    ed.history.seal();
    return true;
  }

  cancel(): void {
    this.editor.setTransient(null);
    this.editor.setOverlay({ guides: [], anchorHint: null });
    this.editor.setUi({ busy: false });
  }
}

function rebuild(line: LineElement, world: Vec[], patch: Partial<LineElement>): LineElement {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of world) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  return {
    ...line,
    ...patch,
    x: x0,
    y: y0,
    w: Math.max(1, x1 - x0),
    h: Math.max(1, y1 - y0),
    pts: world.map((p) => [p.x - x0, p.y - y0] as [number, number])
  };
}

function bestSnap(values: number[], targets: number[], tol: number): { delta: number; at: number } | null {
  let best: { delta: number; at: number } | null = null;
  for (const v of values) {
    for (const t of targets) {
      const d = t - v;
      if (Math.abs(d) <= tol && (!best || Math.abs(d) < Math.abs(best.delta))) best = { delta: d, at: t };
    }
  }
  return best;
}
